import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Paper,
  Collapse,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  ScatterChart,
  Scatter,
  ZAxis,
  ReferenceLine,
} from "recharts";
import { formatearImporte } from "./pygAnaliticoBuild";
import {
  buildMarcasChartRows,
  countMarcasByPrioridad,
  filterMarcasByPrioridad,
} from "./marcaInteresUtils";
import { useTranslation } from "react-i18next";
import {
  pygPriorityLabel,
  translateMarcaReason,
} from "../../constants/pygLineI18n";
import ParetoMarcaChart from "./ParetoMarcaChart";

const TRAMO_EJE = 50000;

const fmtAxis = (v) =>
  typeof v === "number"
    ? v.toLocaleString("es-ES", { maximumFractionDigits: 0 })
    : v;

/** Ticks cada 50.000, incluyendo 0 si hay valores positivos y negativos */
const ticksTramos50k = (values) => {
  const nums = values.filter((n) => Number.isFinite(n));
  if (!nums.length) return [-TRAMO_EJE, 0, TRAMO_EJE];
  let lo = Math.min(0, ...nums);
  let hi = Math.max(0, ...nums);
  lo = Math.floor(lo / TRAMO_EJE) * TRAMO_EJE;
  hi = Math.ceil(hi / TRAMO_EJE) * TRAMO_EJE;
  if (lo === hi) hi += TRAMO_EJE;
  const ticks = [];
  for (let v = lo; v <= hi; v += TRAMO_EJE) ticks.push(v);
  return ticks;
};

const domainDesdeTicks = (ticks) => [ticks[0], ticks[ticks.length - 1]];

const fmtPct = (v) =>
  typeof v === "number"
    ? `${v.toLocaleString("es-ES", { maximumFractionDigits: 1 })} %`
    : "—";

const prioridadColor = (prioridad, theme) => {
  if (prioridad === "alta") return theme.palette.success.main;
  if (prioridad === "media") return theme.palette.warning.main;
  return theme.palette.grey[500];
};

const prioridadChipSx = (prioridad, theme) => {
  const c = prioridadColor(prioridad, theme);
  return {
    height: 22,
    fontSize: "0.7rem",
    fontWeight: 700,
    bgcolor: alpha(c, 0.18),
    color: prioridad === "baja" ? "text.secondary" : undefined,
    border: "1px solid",
    borderColor: alpha(c, 0.45),
  };
};

/** Verde/rojo según signo; cero en gris. Fondo suave para lectura rápida. */
const valorImporteSx = (valor, theme, { bold = false } = {}) => {
  const n = Number(valor) || 0;
  if (n === 0) {
    return { color: "text.secondary", fontWeight: bold ? 700 : 400 };
  }
  const positive = n > 0;
  const palette = positive ? theme.palette.success : theme.palette.error;
  return {
    color: positive ? "success.dark" : "error.main",
    fontWeight: bold ? 700 : 600,
    bgcolor: alpha(palette.main, 0.1),
  };
};

const valorPctSx = (valor, theme, { bold = false } = {}) => {
  const n = Number(valor) || 0;
  if (n === 0) {
    return { color: "text.secondary", fontWeight: bold ? 700 : 400 };
  }
  const positive = n > 0;
  const palette = positive ? theme.palette.success : theme.palette.error;
  return {
    color: positive ? "success.dark" : "error.main",
    fontWeight: bold ? 700 : 600,
    bgcolor: alpha(palette.main, 0.1),
  };
};

const ImporteCell = ({ valor, theme, bold, ...rest }) => (
  <TableCell
    align="right"
    sx={valorImporteSx(valor, theme, { bold })}
    {...rest}
  >
    {formatearImporte(valor)}
  </TableCell>
);

const ChartTooltip = ({ active, payload }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const prioridadLabel = pygPriorityLabel(t, d.prioridad, d.prioridadLabel);
  const razones = (d.razones || [])
    .map((r) => translateMarcaReason(t, r))
    .join(" · ");
  return (
    <Paper sx={{ p: 1.5, maxWidth: 320 }} elevation={4}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
        {d.marca}
      </Typography>
      <Chip
        size="small"
        label={prioridadLabel}
        sx={prioridadChipSx(d.prioridad, theme)}
      />
      <Stack spacing={0.35} sx={{ mt: 1 }}>
        <Typography variant="caption" display="block">
          <strong>{t("pygAnalitico.marcasCharts.tooltipResult")}:</strong>{" "}
          {formatearImporte(d.resultado)}
        </Typography>
        <Typography variant="caption" display="block">
          {t("pygAnalitico.marcasCharts.tooltipSales", {
            venta: formatearImporte(d.venta),
            proy: formatearImporte(d.proyeccionVentas),
          })}
        </Typography>
        <Typography variant="caption" display="block">
          {t("pygAnalitico.marcasCharts.tooltipPurch", {
            compra: formatearImporte(d.compraContable),
            proy: formatearImporte(d.proyeccionCompras),
          })}
        </Typography>
        <Typography variant="caption" display="block">
          {t("pygAnalitico.marcasCharts.tooltipMargin", {
            pct: fmtPct(d.margenSobreVentaPct),
          })}
        </Typography>
        <Typography variant="caption" display="block">
          {t("pygAnalitico.marcasCharts.tooltipOrders", {
            pv: formatearImporte(d.pedidosVenta),
            pc: formatearImporte(d.pedidosCompra),
          })}
        </Typography>
      </Stack>
      {razones ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block", lineHeight: 1.35 }}
        >
          {razones}
        </Typography>
      ) : null}
    </Paper>
  );
};

const FILTRO_I18N = {
  todas: "pygAnalitico.marcasCharts.filterAll",
  alta: "pygAnalitico.marcasCharts.filterAlta",
  media: "pygAnalitico.marcasCharts.filterMedia",
  baja: "pygAnalitico.marcasCharts.filterBaja",
};

const MarcasDetalleTable = ({ rows, theme, onSelectMarca }) => {
  const { t } = useTranslation();
  return (
  <TableContainer sx={{ /* maxHeight: 340, */ mt: 1 }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700 }}>
            {t("pygAnalitico.marcasCharts.colBrand")}
          </TableCell>
          <TableCell sx={{ fontWeight: 700 }}>
            {t("pygAnalitico.marcasCharts.colPriority")}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>
            {t("pygAnalitico.marcasCharts.colSalesInv")}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>
            {t("pygAnalitico.marcasCharts.colSalesProj")}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>
            {t("pygAnalitico.marcasCharts.colSalesOrders")}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>
            {t("pygAnalitico.marcasCharts.colPurchInv")}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>
            {t("pygAnalitico.marcasCharts.colPurchProj")}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>
            {t("pygAnalitico.marcasCharts.colPurchOrders")}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>
            {t("pygAnalitico.marcasCharts.colResult")}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>
            {t("pygAnalitico.marcasCharts.colMargin")}
          </TableCell>
          <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>
            {t("pygAnalitico.marcasCharts.colWhy")}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow
            key={r.marca}
            hover
            sx={{ cursor: onSelectMarca ? "pointer" : "default" }}
            onClick={() => onSelectMarca?.(r.marca)}
          >
            <TableCell sx={{ fontWeight: 600, maxWidth: 160 }}>
              {r.marca}
            </TableCell>
            <TableCell>
              <Chip
                size="small"
                label={pygPriorityLabel(t, r.prioridad, r.prioridadLabel)}
                sx={prioridadChipSx(r.prioridad, theme)}
              />
            </TableCell>
            <ImporteCell valor={r.venta} theme={theme} />
            <ImporteCell valor={r.proyeccionVentas} theme={theme} />
            <ImporteCell valor={r.pedidosVenta} theme={theme} />
            <ImporteCell valor={r.compraContable} theme={theme} />
            <ImporteCell valor={r.proyeccionCompras} theme={theme} />
            <ImporteCell valor={r.pedidosCompra} theme={theme} />
            <ImporteCell valor={r.resultado} theme={theme} bold />
            <TableCell
              align="right"
              sx={valorPctSx(r.margenSobreVentaPct, theme, { bold: true })}
            >
              {fmtPct(r.margenSobreVentaPct)}
            </TableCell>
            <TableCell>
              <Tooltip
                title={(r.razones || []).map((x) => translateMarcaReason(t, x)).join(" · ") || ""}
                placement="left"
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {(r.razones || [])
                    .slice(0, 2)
                    .map((x) => translateMarcaReason(t, x))
                    .join(" · ") || "—"}
                </Typography>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
  );
};

const AnaliticoMarcasCharts = ({
  marcas,
  marcasKpi,
  loadingKpi,
  year,
  onSelectMarca,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [filtro, setFiltro] = useState("todas");
  const [criteriosOpen, setCriteriosOpen] = useState(false);

  const rows = useMemo(
    () => buildMarcasChartRows(marcas, marcasKpi),
    [marcas, marcasKpi],
  );

  const counts = useMemo(() => countMarcasByPrioridad(rows), [rows]);

  const filtered = useMemo(
    () => filterMarcasByPrioridad(rows, filtro),
    [rows, filtro],
  );

  const tablaRows = useMemo(
    () => [...filtered].sort((a, b) => b.resultado - a.resultado),
    [filtered],
  );

  const scatterData = useMemo(
    () =>
      filtered.map((r) => ({
        ...r,
        x: r.pedidosVenta,
        y: r.resultado,
        z: Math.max(Math.abs(r.venta), 1000),
      })),
    [filtered],
  );

  const scatterXTicks = useMemo(
    () => ticksTramos50k(scatterData.map((d) => d.x)),
    [scatterData],
  );

  const scatterYTicks = useMemo(
    () => ticksTramos50k(scatterData.map((d) => d.y)),
    [scatterData],
  );

  if (loadingKpi && rows.length === 0) {
    return (
      <Box
        sx={{
          mb: 2,
          py: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <CircularProgress size={28} />
        <Typography color="text.secondary">
          {t("pygAnalitico.marcasCharts.loadingCalc")}
        </Typography>
      </Box>
    );
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <Box
      id="analitico-charts"
      sx={{
        mb: 2.5,
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.9),
        bgcolor: alpha(theme.palette.background.paper, 0.85),
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "flex-start" }}
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {t("pygAnalitico.marcasCharts.focusTitle", { year })}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {t("pygAnalitico.marcasCharts.focusHint")}
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<InfoOutlinedIcon />}
          endIcon={
            <ExpandMoreIcon
              sx={{
                transform: criteriosOpen ? "rotate(180deg)" : "none",
                transition: "0.2s",
              }}
            />
          }
          onClick={() => setCriteriosOpen((o) => !o)}
          sx={{ textTransform: "none", flexShrink: 0 }}
        >
          {t("pygAnalitico.marcasCharts.howClassified")}
        </Button>
      </Stack>

      <Collapse in={criteriosOpen}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 1.5,
            bgcolor: alpha(theme.palette.info.main, 0.04),
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
            {t("pygAnalitico.marcasCharts.threeLabels")}
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Typography variant="caption" display="block">
              {t("pygAnalitico.marcasCharts.criteriaAlta")}
            </Typography>
            <Typography variant="caption" display="block">
              {t("pygAnalitico.marcasCharts.criteriaMedia")}
            </Typography>
            <Typography variant="caption" display="block">
              {t("pygAnalitico.marcasCharts.criteriaBaja")}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t("pygAnalitico.marcasCharts.criteriaFormula")}
          </Typography>
        </Paper>
      </Collapse>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1}
        sx={{ mb: 1 }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filtro}
          onChange={(_e, v) => v && setFiltro(v)}
          sx={{ flexWrap: "wrap" }}
        >
          <ToggleButton value="todas">
            {t("pygAnalitico.marcasCharts.toggleAll", { count: counts.total })}
          </ToggleButton>
          <ToggleButton value="alta">
            {t("pygAnalitico.marcasCharts.toggleAlta", { count: counts.alta })}
          </ToggleButton>
          <ToggleButton value="media">
            {t("pygAnalitico.marcasCharts.toggleMedia", { count: counts.media })}
          </ToggleButton>
          <ToggleButton value="baja">
            {t("pygAnalitico.marcasCharts.toggleBaja", { count: counts.baja })}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5 }}
      >
        {t(FILTRO_I18N[filtro])} ·{" "}
        {t("pygAnalitico.marcasCharts.showingCount", {
          shown: filtered.length,
          total: counts.total,
        })}
      </Typography>

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ flexWrap: "wrap", mb: 2 }}
      >
        <Chip
          size="small"
          clickable
          onClick={() => setFiltro("alta")}
          label={t("pygAnalitico.marcasCharts.interesting", { count: counts.alta })}
          sx={{
            fontWeight: 700,
            bgcolor: alpha(theme.palette.success.main, 0.15),
          }}
        />
        <Chip
          size="small"
          clickable
          onClick={() => setFiltro("media")}
          label={t("pygAnalitico.marcasCharts.watch", { count: counts.media })}
          sx={{
            fontWeight: 600,
            bgcolor: alpha(theme.palette.warning.main, 0.12),
          }}
        />
        <Chip
          size="small"
          clickable
          onClick={() => setFiltro("baja")}
          label={t("pygAnalitico.marcasCharts.low", { count: counts.baja })}
          variant="outlined"
        />
        <Chip
          size="small"
          label={t("pygAnalitico.marcasCharts.positiveResult", {
            count: rows.filter((r) => r.resultado > 0).length,
          })}
          variant="outlined"
        />
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        <Paper variant="outlined" sx={{ p: 1.5, minHeight: 320, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t("pygAnalitico.marcasCharts.mapTitle")}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mb: 1 }}
          >
            {t("pygAnalitico.marcasCharts.mapHint")}
          </Typography>
          <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            sx={{ mb: 0.5, flexWrap: "wrap" }}
          >
            {["alta", "media", "baja"].map((p) => (
              <Chip
                key={p}
                size="small"
                label={pygPriorityLabel(t, p)}
                sx={{ height: 20, ...prioridadChipSx(p, theme) }}
              />
            ))}
          </Stack>
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="x"
                tickFormatter={fmtAxis}
                ticks={scatterXTicks}
                domain={domainDesdeTicks(scatterXTicks)}
                allowDataOverflow
                tick={{ fontSize: 9 }}
              />
              <YAxis
                type="number"
                dataKey="y"
                tickFormatter={fmtAxis}
                ticks={scatterYTicks}
                domain={domainDesdeTicks(scatterYTicks)}
                allowDataOverflow
                tick={{ fontSize: 9 }}
                width={44}
              />
              <ZAxis type="number" dataKey="z" range={[40, 400]} />
              <RechartsTooltip
                content={<ChartTooltip />}
                cursor={{ strokeDasharray: "3 3" }}
              />
              <ReferenceLine y={0} stroke={theme.palette.divider} />
              {["alta", "media", "baja"].map((p) => (
                <Scatter
                  key={p}
                  name={pygPriorityLabel(t, p)}
                  data={scatterData.filter((d) => d.prioridad === p)}
                  fill={prioridadColor(p, theme)}
                  cursor="pointer"
                  onClick={(d) => onSelectMarca?.(d?.marca)}
                />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </Paper>

        <Box sx={{ minWidth: 0 }}>
          <ParetoMarcaChart
            key="pareto-venta"
            rows={rows}
            metric="venta"
            title={t("pygAnalitico.marcasCharts.paretoSalesTitle")}
            subtitle={t("pygAnalitico.marcasCharts.paretoSalesSubtitle")}
            valorLabel={t("pygAnalitico.marcasCharts.paretoSalesValue")}
            barColor={theme.palette.success.main}
            onSelectMarca={onSelectMarca}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <ParetoMarcaChart
            key="pareto-resultado"
            rows={rows}
            metric="resultado"
            title={t("pygAnalitico.marcasCharts.paretoResultTitle")}
            subtitle={t("pygAnalitico.marcasCharts.paretoResultSubtitle")}
            valorLabel={t("pygAnalitico.marcasCharts.paretoResultValue")}
            barColor={theme.palette.info.main}
            onSelectMarca={onSelectMarca}
          />
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
          {t("pygAnalitico.marcasCharts.detailTitle")}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 0.5 }}
        >
          {t("pygAnalitico.marcasCharts.detailHint")}
        </Typography>
        <MarcasDetalleTable
          rows={tablaRows}
          theme={theme}
          onSelectMarca={onSelectMarca}
        />
      </Paper>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: "block" }}
      >
        {t("pygAnalitico.marcasCharts.clickHint")}
      </Typography>
    </Box>
  );
};

export default AnaliticoMarcasCharts;
